import {type Dispatch, type SetStateAction, useEffect, useState} from 'react';

import {StyledSpacerRow} from 'styles/InputMenu';
import InputActionButtons from './InputActionButtons';

import useParseURL from 'hooks/useParseURL';

import {useGetDelousing, useGetPlan, useGetSiteCages, useGetSites} from 'hooks/swr';

import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import {useTranslation} from 'react-i18next';
import backendCommunicator from 'services/backendCommunicator';
import {dateReader, localDateSender} from 'helpers/UnitsFormatHandler';
import Alert from 'helpers/Alert';
import {useInputMenuContext} from 'context/InputMenu';
import {
  type AddDelousingProcessed,
  type InputMenuAction,
  inputMenuActionType,
  type InputMenuState,
  methodType,
} from 'interfaces';
import {handleKeyDown} from './helpers';
import {mutateCage} from 'helpers/refreshHandler';
import {type CageResponse} from 'client';
import GenericTextField from 'components/Generic/TextField';
import NumberFormatCustom from 'helpers/NumberFormatter';
import useGetLongTermRange from 'hooks/useGetLongTermRange';
import {DELOUSING_MORTALITY_RATIO} from 'appConstants';
import {NumericField} from 'components/Generic/NumericField';
import {useGetIsChilePlan} from 'hooks/useGetIsChilePlan';
import {useFlags} from 'launchdarkly-react-client-sdk';

export const DelousingInputMenu = () => {
  const {t} = useTranslation('common');

  const {planId} = useParseURL();
  const {delousingCost: delousingCostEnabled} = useFlags();
  const {data: plan} = useGetPlan({resourceId: planId});
  const {data: sites} = useGetSites({resourceId: planId});
  const dateObject = plan ? dateReader(plan?.startDate) : new Date();
  const {startDate, endDate} = useGetLongTermRange();
  const {isChilePlan, conversionRate} = useGetIsChilePlan();
  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const inputMenuState = inputMenuContext?.state;
  const initialState = {
    cageId: inputMenuContext?.state.formData?.cageId ?? 'all',
    siteId: inputMenuContext?.state.formData?.siteId ?? '',
    dateObject: inputMenuContext?.state.formData?.date ?? dateObject,
    stringedMortalityRatio: `${DELOUSING_MORTALITY_RATIO}`,
    date: '',
    id: '',
    cost: 0,
    mortalityRatio: DELOUSING_MORTALITY_RATIO,
  };
  const [delousing, setDelousing] = useState<AddDelousingProcessed>(initialState);
  const {data: cages} = useGetSiteCages({resourceId: delousing.siteId || undefined});

  const {data: delousingToEdit} = useGetDelousing({resourceId: inputMenuState?.formData?.id ?? undefined});
  const [createUpdateDelousing, setCreateUpdateDelousing] = useState<boolean>(false);

  useEffect(() => {
    if (inputMenuState?.method === methodType.edit && delousingToEdit) {
      const mortalityRatio = Math.round(delousingToEdit.mortalityRatio * 1000000) / 10000;
      setDelousing({
        ...delousingToEdit,
        dateObject: dateReader(delousingToEdit.date),
        mortalityRatio,
        stringedMortalityRatio: mortalityRatio.toString(),
        cost: delousingToEdit.cost * conversionRate,
      });
    }
  }, [delousingToEdit]);

  const disabled = delousing.stringedMortalityRatio === '';

  useEffect(() => {
    createUpdateDelousing &&
      inputMenuDispatch &&
      inputMenuState &&
      startDate &&
      endDate &&
      addDelousingHandler({
        delousing,
        inputMenuDispatch,
        inputMenuState,
        setCreateUpdateDelousing,
        cages,
        conversionRate,
      });
  }, [createUpdateDelousing, inputMenuState?.method]);

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      {/* Site Picker */}
      <GenericSelect
        label={t('site')}
        value={delousing.siteId || ''}
        onChange={(event) => {
          const siteId = event.target.value as string;
          setDelousing({...delousing, siteId, cageId: ''});
        }}
        aria-label={t('site')}
        data-testid="site"
        disabled={inputMenuState?.method === methodType.edit}
      >
        <GenericSelectItem value={undefined}>
          <em>{t('noneSelected')}</em>
        </GenericSelectItem>
        {sites?.map((site) => (
          <GenericSelectItem value={site.id} key={site.id}>
            {site.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>
      <StyledSpacerRow />

      {/* Cage Picker */}
      <GenericSelect
        label={t('cage')}
        aria-label={t('cage')}
        value={delousing.cageId}
        onChange={(event) => {
          const cageId = event.target.value as string;
          setDelousing({...delousing, cageId});
        }}
        data-testid="cage"
        disabled={inputMenuState?.method === methodType.edit}
      >
        <GenericSelectItem value={'all'}>{t('all')}</GenericSelectItem>
        {cages?.map((cage) => (
          <GenericSelectItem key={cage.id} value={cage.id}>
            {cage.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>
      <StyledSpacerRow />

      <InputMenuDatePicker
        value={delousing.dateObject}
        onChange={(date) => {
          date && setDelousing({...delousing, dateObject: date});
        }}
        slotProps={{
          textField: {
            onKeyDown: (event) => {
              inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateDelousing);
            },
          },
        }}
      />
      <StyledSpacerRow />

      <GenericTextField
        InputProps={{
          inputComponent: NumberFormatCustom as (props) => JSX.Element,
          disableUnderline: false,
          endAdornment: <div>%</div>,
          style: {textAlign: 'right'},
        }}
        value={delousing.stringedMortalityRatio}
        onChange={(event) => {
          const pattern = /[0-9]+([,.][0-9]+)?/;
          if (pattern.test(event.target.value) || event.target.value === '') {
            setDelousing({...delousing, stringedMortalityRatio: event.target.value});
          }
        }}
        label={t('mortalityDelousingLabel')}
      />
      <StyledSpacerRow />
      {/* delousing cost */}
      {(delousingCostEnabled || isChilePlan) && (
        <NumericField
          handleChange={({target: {value}}) => {
            setDelousing({...delousing, cost: parseFloat(value)});
          }}
          onKeyPress={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateDelousing);
          }}
          value={delousing?.cost}
          fieldName="delousingCost"
          helperText="delousingCostHelperText"
          endAdornment={t('NOK')}
        />
      )}
      <StyledSpacerRow />
      <InputActionButtons
        setCreateUpdateInput={setCreateUpdateDelousing}
        disabled={disabled}
        loading={createUpdateDelousing}
      />
    </form>
  );
};

const addDelousingHandler = ({
  delousing,
  inputMenuDispatch,
  inputMenuState,
  setCreateUpdateDelousing,
  cages,
  conversionRate,
}: {
  delousing: AddDelousingProcessed;
  inputMenuDispatch: Dispatch<InputMenuAction>;
  inputMenuState: InputMenuState;
  setCreateUpdateDelousing: Dispatch<SetStateAction<boolean>>;
  cages?: CageResponse[];
  conversionRate: number;
}) => {
  const request = async () => {
    const date = localDateSender(delousing.dateObject);
    const mortalityRatio = delousing.stringedMortalityRatio
      ? Math.round(parseFloat(delousing.stringedMortalityRatio.replace(',', '.')) * 1000) / 100000
      : 0;
    const cost = delousing.cost / conversionRate;
    if (inputMenuState?.method === methodType.add && delousing.cageId !== 'all') {
      return await backendCommunicator.addDelousing({
        date,
        cageId: delousing.cageId,
        mortalityRatio,
        cost,
      });
    } else if (inputMenuState?.method === methodType.add) {
      return await backendCommunicator.addSiteDelousing({
        date,
        siteId: delousing.siteId,
        mortalityRatio,
        cost,
      });
    } else {
      return await backendCommunicator.updateDelousing(delousing.id, {
        date: localDateSender(delousing.dateObject),
        mortalityRatio,
        cost,
      });
    }
  };

  request()
    .then(({data}) => {
      inputMenuState?.onClose && inputMenuState.onClose(data);
      if (Array.isArray(data)) {
        data.forEach((delousingData) => {
          mutateCage(delousingData.cageId);
        });
      } else {
        mutateCage(data.cageId);
      }
      cages?.forEach((cage) => {
        mutateCage(cage.id);
      });
      inputMenuDispatch({type: inputMenuActionType.closeMenu});
    })
    .catch((error) => {
      Alert.error(error);
    })
    .finally(() => {
      setCreateUpdateDelousing(false);
    });
};
